/**
 * strategicPositioning.js
 *
 * Composes a per-zone strategic-positioning view: how much store attention
 * each product zone gets vs how much € each visit realises. Answers the
 * merchandising question "which zones deserve floor space / signage /
 * promotion?" — a different decision than the funnel-quality optimisation
 * the PatternMatrix supports on the Prioritization tab.
 *
 * Pure function — no Vue, no DOM, no I/O — so it can be unit-tested and
 * reused outside the demo view.
 *
 * Axes:
 *   X = visitor share %  (zoneVisitors / storeTotalVisitors * 100)
 *   Y = € per visitor    (engagement × conversion × avg product value)
 *
 * Quadrant split: medians of the current zones. This makes the chart
 * adaptive to the store — a zone is a "Star" relative to its siblings,
 * not vs. an arbitrary absolute threshold we don't have a benchmark for.
 *
 *   ⭐ Stars              — high traffic + high € per visitor (winners)
 *   🏃 High Runners       — high traffic + low € per visitor (volume drivers)
 *   💡 Spotlight Candidates — low traffic + high € per visitor (untapped)
 *   🧊 Cold Spots         — low traffic + low € per visitor (review)
 *
 * Labels deliberately avoid the PatternMatrix vocabulary
 * (Engagement gap / Conversion gap / Reach gap / Mid-tier / Performing
 * well) to prevent confusion between the two scatter charts even though
 * they live in different tabs.
 */

import { PATTERN_META } from './settings'
import { extractProductZones } from './priorities'
import { classifyPattern } from './classifier'
import { resolveZoneCeiling } from './ceilingsRef'

export const QUADRANT_META = {
  star: {
    key: 'star',
    label: 'Stars',
    icon: '⭐',
    color: '#36910d',
    legend: '⭐ Stars · high traffic, high € per visitor',
    interp: "Your store's hero zones. Protect layout, replicate the format elsewhere.",
  },
  runner: {
    key: 'runner',
    label: 'High Runners',
    icon: '🏃',
    color: '#4F7EB3',
    legend: '🏃 High Runners · high traffic, low € per visitor',
    interp: 'Volume drivers — visitors come but spend little here. Upsell, bundle, or shift the SKU mix.',
  },
  spotlight: {
    key: 'spotlight',
    label: 'Spotlight Candidates',
    icon: '💡',
    color: '#D3A741',
    legend: '💡 Spotlight Candidates · low traffic, high € per visitor',
    interp: 'High-margin zones too few people see. Move closer to traffic flow, signage, end-cap, etalage.',
  },
  cold: {
    key: 'cold',
    label: 'Cold Spots',
    icon: '🧊',
    color: '#9aa7b3',
    legend: '🧊 Cold Spots · low traffic, low € per visitor',
    interp: 'Review purpose, placement, or whether the SKU mix earns the floor space.',
  },
}

function median(arr) {
  if (!arr.length) return 0
  const s = [...arr].sort((a, b) => a - b)
  const m = s.length / 2
  return s.length % 2 ? s[Math.floor(m)] : (s[m - 1] + s[m]) / 2
}

function classifyQuadrant(visitorShare, eurPerVisitor, medians) {
  // Strict-greater-than tie-break so a zone exactly on a median is treated
  // as the *lower* of the two regions — keeps "Star" reserved for zones
  // genuinely above the centre.
  const highX = visitorShare > medians.x
  const highY = eurPerVisitor > medians.y
  if (highX && highY)  return 'star'
  if (highX && !highY) return 'runner'
  if (!highX && highY) return 'spotlight'
  return 'cold'
}

/**
 * @param {Object} fixedWidgetsData — the same payload TodayHero / gauges read
 * @param {string} segmentKey — company-wide sector (CompanySettings.sector)
 * @param {Object} zoneOverrides — {zoneName: sectorOverride} from
 *        buildZoneOverridesMap(detectionZoneStore). Per-zone overrides
 *        flow through resolveZoneCeiling per zone.
 * @returns { hasData, points, medians, ceiling: { Emax, Cmax } }
 *
 * Note: the returned top-level `ceiling` is the COMPANY-level Level-1
 * default (sector × 'product') for compatibility with consumers that
 * render a single reference value. Per-zone ceilings used in the math
 * are resolved internally and exposed as `point.ceiling` per zone.
 */
export function composeStrategicPositioning(fixedWidgetsData, segmentKey = 'default', zoneOverrides = {}) {
  const ceiling = resolveZoneCeiling(segmentKey, null, 'product')
  const storeTotal = Number(fixedWidgetsData?.store_total_visitors || 0)

  // extractProductZones already normalises conversion (0–1) and engagement,
  // and resolves avg_product_value per zone — reuse to keep math consistent
  // with the Prio panel + StoreEfficiencyGauge.
  const allZones = extractProductZones(fixedWidgetsData || {})
    .filter(z => z.visitors > 0)
  // platform_capabilities.md §17.7 Phase 2 — €/visitor is undefined for a counts-only tripwire zone (no
  // measured engagement×conversion). Exclude such zones from the quadrant so they
  // don't all collapse onto the €0 axis; when EVERY zone is counts-only (a footfall
  // store), flag notMeasurable and render no quadrant.
  const zones = allZones.filter(z => (z.capability || {}).engagementMeasurable !== false)
  const notMeasurable = allZones.length > 0 && zones.length === 0

  if (!storeTotal || zones.length === 0) {
    return {
      hasData: false,
      points: [],
      medians: { x: 0, y: 0 },
      ceiling,
      notMeasurable,
    }
  }

  // First pass: compute axis values per zone, using the zone's own ceiling
  // (sector override → company sector → DEFAULT_SECTOR) for both the
  // classifyPattern verdict and the per-zone ceilingEurPerVisitor.
  // The funnel pattern is included as *descriptive* cross-chart context
  // (rendered in tooltips so the Intel-tab user sees what the Prio-tab
  // PatternMatrix says about the same zone). This composer never emits
  // a "top focus" ranking — that's Prio-tab territory by design.
  const raw = zones.map(z => {
    const zoneCeiling = resolveZoneCeiling(segmentKey, zoneOverrides[z.zone], 'product')
    const visitorShare = (z.visitors / storeTotal) * 100
    const eurPerVisitor = z.engagementRate * z.conversionRate * z.avgValue
    const ceilingEurPerVisitor = zoneCeiling.Emax * zoneCeiling.Cmax * z.avgValue
    const pattern = classifyPattern(z, zoneCeiling)
    return {
      zone: z.zone,
      visitors: z.visitors,
      engagementRate: z.engagementRate,
      conversionRate: z.conversionRate,
      avgValue: z.avgValue,
      visitorShare,
      eurPerVisitor,
      ceilingEurPerVisitor,
      ceiling: zoneCeiling,
      headroom: Math.max(0, ceilingEurPerVisitor - eurPerVisitor),
      patternKind: pattern.kind,
      patternLabel: PATTERN_META[pattern.kind]?.legend || pattern.label,
      patternColor: PATTERN_META[pattern.kind]?.color || '#7a8895',
    }
  })

  // Quadrant split = medians across the zones we have.
  const medians = {
    x: median(raw.map(r => r.visitorShare)),
    y: median(raw.map(r => r.eurPerVisitor)),
  }

  // Second pass: classify into quadrants.
  const points = raw.map(r => ({
    ...r,
    quadrant: classifyQuadrant(r.visitorShare, r.eurPerVisitor, medians),
  }))

  return {
    hasData: true,
    points,
    medians,
    ceiling,
    notMeasurable: false,
  }
}
